import { TranslateModule } from '@ngx-translate/core';
import { afterNextRender, Component, DestroyRef, effect, inject, input, model, signal, untracked } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import * as atlas from 'azure-maps-control';
import { environment } from '../../../../environments/environment';
import { AzureMapPosition } from './azure-map.model';
import { AzureMapService } from './azure-map.service';
import { CustomControl } from './map-controls/azure-map.control';

@Component({
  selector: 'cipo-azure-map',
  templateUrl: './azure-map.component.html',
  styleUrls: ['./azure-map.component.scss'],
  imports: [MatIconModule, MatButtonModule, TranslateModule],
})
export class AzureMapComponent {
  private azureMapService = inject(AzureMapService);
  private destroyRef = inject(DestroyRef);
  private destroyed = false;

  editMode = input<boolean>(false);
  multiple = input<boolean>(false);
  coordinates = model<AzureMapPosition[]>([]);
  selected = model<number>();

  private map: atlas.Map;
  private pins: atlas.HtmlMarker[] = [];
  private coordinatePrecision = 6;
  private zoomLevel = 16;
  private mapReady = false;
  private currentPosition: AzureMapPosition | null = null;

  mapId = signal(this.uniqueId());

  afterRenderRef = afterNextRender(() => {
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
    });
    this.getCurrentPosition(() => {
      // Initialize the map after the current position is obtained (or permission is denied)
      // if the component is already destroyed by the time we get here, skip map initialization
      if (!this.destroyed) {
        this.initializeMap();
      }
    });
  });

  coordinatesChangeEffect = effect(() => {
    const coords = this.coordinates();
    untracked(() => {
      this.addPinsToMap(coords);
    });
  });

  selectionChangeEffect = effect(() => {
    const selectedIndex = this.selected();
    untracked(() => {
      this.updatePinSelection(selectedIndex);
    });
  });

  private addPinsToMap(coords: AzureMapPosition[]) {
    if (!this.map || !this.mapReady) {
      return;
    }

    let useDefaultCoords = false;
    const noCoordsProvided = !coords || coords.length === 0;

    // add current location as default if no coordinates provided
    if (noCoordsProvided) {
      coords = [{ lat: 0, lon: 0 }];
      useDefaultCoords = true;
    }

    // try to use the current position if it exists, for the last empty coordinate
    let lastCoord = coords.at(-1);
    if (!lastCoord.lat && !lastCoord.lon && this.currentPosition) {
      lastCoord = { ...this.currentPosition };
      useDefaultCoords = true;
    }

    this.updatePins(coords);
    this.fitMapToAllMarkers();

    // mark coordinates with current location as changed
    if (useDefaultCoords) {
      this.updateCoordinate(lastCoord, coords.length - 1);
    }
  }

  private getCurrentPosition(callback: () => void) {
    // Check if Permissions API is available
    if ('permissions' in navigator) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then(permissionStatus => {
          if (permissionStatus.state === 'denied') {
            callback();
            return;
          }

          // Permission is granted or prompt - proceed with geolocation
          this.requestGeolocation(callback);
        })
        .catch(() => {
          // Permissions API failed, do not block and try geolocation
          callback();
        });
    } else {
      // Permissions API not available, do not block and try geolocation
      callback();
    }
  }

  private requestGeolocation(callback: () => void) {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        // Successfully obtained position
        this.currentPosition = {
          lat: this.truncateCoordinate(coords.latitude),
          lon: this.truncateCoordinate(coords.longitude),
        };
        callback();
      },
      () => {
        // User denied permission, timeout, or other error - use default fallback
        callback();
      },
      {
        timeout: 10000, // wait up to 10 seconds
      },
    );
  }

  setMapToCurrentLocation() {
    if (this.currentPosition) {
      this.setMapCenter({ lat: this.currentPosition.lat, lon: this.currentPosition.lon });
    } else {
      this.getCurrentPosition(() => {
        if (this.currentPosition) {
          this.setMapCenter({ lat: this.currentPosition.lat, lon: this.currentPosition.lon });
        }
      });
    }
  }

  fitMapToAllMarkers() {
    if (!this.map || !this.pins || this.pins.length === 0) {
      return;
    }

    if (this.multiple() && this.pins.length > 1) {
      const positions = this.pins.map(pin => pin.getOptions().position as atlas.data.Position);
      const boundingBox = atlas.data.BoundingBox.fromPositions(positions);

      this.map.setCamera({
        bounds: boundingBox,
        padding: 200,
      });
    } else if (this.pins.length === 1) {
      const position = this.pins[0].getOptions().position as atlas.data.Position;
      this.map.setCamera({
        center: position,
        zoom: this.zoomLevel,
      });
    }
  }

  private initializeMap() {
    this.map = new atlas.Map(this.mapId(), {
      zoom: this.zoomLevel,
      showFeedbackLink: false,
      authOptions: {
        authType: atlas.AuthenticationType.subscriptionKey,
        subscriptionKey: environment.azureMapsSubscriptionKey,
      },
      showLogo: false,
    });
    this.map.events.add('ready', () => {
      this.mapReady = true;
      this.addMapControls();
      this.addPinsToMap(this.coordinates());
    });
  }

  private setMapCenter({ lat, lon }: AzureMapPosition) {
    this.updateCoordinate({ lat, lon }, this.selected() ?? 0);
    this.map.setCamera({
      center: new atlas.data.Position(lon, lat),
      zoom: this.zoomLevel,
    });
  }

  private addMapControls() {
    const mapControls = document.getElementById('mapControls');
    if (mapControls) {
      Array.from(mapControls.children).forEach(child => {
        const customControl = new CustomControl(child as HTMLElement);
        this.map.controls.add(customControl, {
          position: 'bottom-right',
        } as atlas.ControlOptions);
      });
    }
  }

  private uniqueId() {
    const dateString = Date.now().toString(36);
    const randomness = Math.random().toString(36).substring(2);
    return dateString + randomness;
  }

  private truncateCoordinate(value: number): number {
    const factor = Math.pow(10, this.coordinatePrecision);
    return Math.round(value * factor) / factor;
  }

  private createPins(coords: AzureMapPosition[]) {
    if (!coords || coords.length === 0) {
      return;
    }

    this.pins = coords.map((pos, index) => {
      const marker = new atlas.HtmlMarker({
        position: new atlas.data.Position(pos.lon ?? 0, pos.lat ?? 0),
        htmlContent: this.azureMapService.createPinElement(index, this.selected() === index, this.multiple()),
        draggable: this.editMode(),
        anchor: 'center',
      });

      marker._addEventListener(
        'click',
        () => {
          this.selected.set(index);
        },
        false,
      );

      if (this.editMode()) {
        marker._addEventListener(
          'dragend',
          e => {
            this.selected.set(index);

            const [lon, lat] = e.target.getOptions().position;
            this.updateCoordinate({ lat, lon }, index);
          },
          false,
        );
      }

      return marker;
    });
  }

  private updateCoordinate(coord: AzureMapPosition, index: number) {
    const updatedCoords = [...this.coordinates()];
    updatedCoords[index] = {
      lat: this.truncateCoordinate(coord.lat),
      lon: this.truncateCoordinate(coord.lon),
    };
    this.coordinates.set(updatedCoords);
  }

  private updatePinSelection(selectedIndex: number) {
    if (this.mapReady && this.pins.length > 0 && selectedIndex !== undefined) {
      this.pins.forEach((pin, index) => {
        pin.setOptions({
          htmlContent: this.azureMapService.createPinElement(index, selectedIndex === index, this.multiple()),
        });
      });
    }
  }

  private updatePins(coords: AzureMapPosition[]) {
    if (!this.map) {
      return;
    }

    // Remove existing pins
    if (this.pins.length > 0) {
      this.map.markers.remove(this.pins);
      this.pins = [];
    }

    // Create new pins
    this.createPins(coords);

    // Add new pins to map
    if (this.pins.length > 0) {
      this.map.markers.add(this.pins);
    }
  }
}
